// Register.js - Registration page component with Tailwind + Shadcn/UI
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { AuthBackground } from '../components/AuthBackground';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLoginLink, setShowLoginLink] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowLoginLink(false);
    setPendingApproval(false);

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please make sure both passwords are the same.');
      return;
    }

    if (password.length < 8) {
      setError('Your password needs to be at least 8 characters long.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await register(email, password, displayName || undefined);
      if (result.success) {
        // Check if registration is pending approval
        if (result.pending_approval) {
          setPendingApproval(true);
        } else {
          navigate('/app', { replace: true });
        }
      } else {
        setError(result.error || 'Registration failed. Please try again.');
        // Show login link if the error suggests the user should log in
        if (result.action === 'login') {
          setShowLoginLink(true);
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show pending approval message
  if (pendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#f7f5fb]">
        <AuthBackground variant="quiet" />
        <Card className="w-full max-w-[420px] relative z-10 border-white/80" style={{boxShadow: '0 30px 80px -28px rgba(82, 61, 120, 0.28)', backgroundColor: 'rgba(255, 255, 255, 0.92)', borderRadius: '16px', backdropFilter: 'blur(16px)'}}>
          <CardHeader className="text-center space-y-4 pb-2">
            <img 
              src="/static/kal-cropped.svg" 
              alt="Kal Logo" 
              className="w-[120px] h-auto mx-auto"
            />
            <div className="flex flex-col items-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
              <CardTitle className="text-2xl font-semibold text-kaleido-gray-900">
                Registration Submitted
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-kaleido-gray-600 mb-4">
              Thank you for registering! Your account is pending administrator approval.
            </p>
            <p className="text-kaleido-gray-500 text-sm">
              You will receive an email once your account has been approved with instructions on how to access the system.
            </p>
          </CardContent>
          <CardFooter className="flex flex-col items-center pt-2 pb-6">
            <Link 
              to="/login" 
              className="text-kaleido-purple font-medium hover:underline"
            >
              Return to login
            </Link>
            <div className="flex flex-col items-center mt-6 opacity-70">
              <span className="text-[10px] text-kaleido-gray-500 mb-1">powered by</span>
              <img 
                src="/static/asset_1b.png" 
                alt="Kaleido Logo" 
                className="h-[18px] w-auto"
              />
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#f7f5fb]">
      <AuthBackground />

      <Card className="w-full max-w-[420px] relative z-10 border-white/80" style={{boxShadow: '0 30px 80px -28px rgba(82, 61, 120, 0.28)', backgroundColor: 'rgba(255, 255, 255, 0.92)', borderRadius: '16px', backdropFilter: 'blur(16px)'}}>
        <CardHeader className="text-center space-y-4 pb-2">
          <img 
            src="/static/kal-cropped.svg" 
            alt="Kal Logo" 
            className="w-[120px] h-auto mx-auto"
          />
          <div>
            <CardTitle className="text-2xl font-semibold text-kaleido-gray-900">
              Create account
            </CardTitle>
            <CardDescription className="text-kaleido-gray-500 mt-1">
              Sign up to get started with Kal
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant={showLoginLink ? "default" : "destructive"} className={showLoginLink ? "bg-amber-50 border-amber-300 text-amber-800" : ""}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="ml-2">
                  <span>{error}</span>
                  {showLoginLink && (
                    <Link 
                      to="/login" 
                      className="block mt-2 text-kaleido-purple font-medium hover:underline"
                    >
                      Sign in to your account →
                    </Link>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-kaleido-gray-700">
                Display Name <span className="text-kaleido-gray-500 font-normal">(optional)</span>
              </Label>
              <Input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                disabled={isLoading}
                className="h-11 border-white/80 bg-white/85 focus-visible:ring-kaleido-purple"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-kaleido-gray-700">
                Email
              </Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                disabled={isLoading}
                className="h-11 border-white/80 bg-white/85 focus-visible:ring-kaleido-purple"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-kaleido-gray-700">
                Password
              </Label>
              <Input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                autoComplete="new-password"
                disabled={isLoading}
                className="h-11 border-white/80 bg-white/85 focus-visible:ring-kaleido-purple"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-kaleido-gray-700">
                Confirm Password
              </Label>
              <Input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                required
                autoComplete="new-password"
                disabled={isLoading}
                className="h-11 border-white/80 bg-white/85 focus-visible:ring-kaleido-purple"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 mt-2 bg-gradient-to-r from-kaleido-purple to-[#8050a0] hover:from-[#8050a0] hover:to-kaleido-purple transition-all duration-300 hover:shadow-lg hover:shadow-kaleido-purple/25"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex flex-col items-center pt-2 pb-6">
          <div className="text-center border-t border-kaleido-gray-100 pt-4 w-full">
            <p className="text-sm text-kaleido-gray-500">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="text-kaleido-purple font-medium hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>

          <div className="flex flex-col items-center mt-6 opacity-70">
            <span className="text-[10px] text-kaleido-gray-500 mb-1">powered by</span>
            <img 
              src="/static/asset_1b.png" 
              alt="Kaleido Logo" 
              className="h-[18px] w-auto"
            />
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

export default Register;
